import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { normalizeCatalogueSearch } from '@erp/shared';
import { SearchCombobox, type ComboboxOption } from './search-combobox';

export interface CatalogueOption extends ComboboxOption {
  search?: string;
  disabled?: boolean;
}
export interface CatalogueSelectProps {
  id?: string | undefined;
  label: string;
  value: string | readonly string[];
  onChange: (value: string[]) => void;
  options: readonly CatalogueOption[];
  multiple?: boolean;
  clearable?: boolean;
  emptyValueLabel?: string | undefined;
  ariaInvalid?: boolean | undefined;
  ariaDescribedBy?: string | undefined;
  disabled?: boolean;
  isLoading?: boolean;
  isError?: boolean;
  onRetry?: (() => void) | undefined;
}
export function CatalogueSelect({
  id,
  label,
  value,
  onChange,
  options,
  multiple = false,
  clearable = true,
  emptyValueLabel,
  ariaInvalid,
  ariaDescribedBy,
  disabled = false,
  isLoading = false,
  isError = false,
  onRetry,
}: CatalogueSelectProps): JSX.Element {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const values = useMemo(
    () => (typeof value === 'string' ? (value ? [value] : []) : value),
    [value],
  );
  const selected = values.map(
    (key) =>
      options.find((option) => option.id === key) ?? {
        id: key,
        label: key,
        hint: t('international.legacyValue'),
      },
  );
  const normalized = normalizeCatalogueSearch(query);
  const filtered = useMemo(
    () =>
      options.filter(
        (option) =>
          (!option.disabled || values.includes(option.id)) &&
          normalizeCatalogueSearch(
            option.search ?? `${option.label} ${option.hint ?? ''} ${option.id}`,
          ).includes(normalized),
      ),
    [options, normalized, values],
  );
  const visible = [
    ...(clearable && !multiple && !normalized
      ? [{ id: '', label: emptyValueLabel ?? t('common.none') }]
      : []),
    ...filtered,
  ];
  return (
    <div className="space-y-1.5">
      {multiple && selected.length > 0 ? (
        <div className="flex flex-wrap gap-1">
          {selected.map((option) => (
            <span
              key={option.id}
              className="inline-flex max-w-full items-center rounded-md border bg-muted text-xs"
            >
              <span className="truncate pl-2" title={option.label}>
                {option.label}
                {option.hint ? (
                  <span className="text-muted-foreground"> · {option.hint}</span>
                ) : null}
              </span>
              <button
                type="button"
                disabled={disabled}
                className="flex h-11 w-11 shrink-0 items-center justify-center rounded focus-visible:ring-2 focus-visible:ring-ring"
                aria-label={t('international.remove', { name: option.label })}
                onClick={() => onChange(values.filter((key) => key !== option.id))}
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      ) : null}
      <SearchCombobox
        id={id}
        ariaLabel={label}
        ariaInvalid={ariaInvalid}
        ariaDescribedBy={ariaDescribedBy}
        placeholder={emptyValueLabel ?? t('common.pleaseSelect')}
        value={multiple ? '' : (selected[0]?.label ?? '')}
        options={visible}
        emptyLabel={t('international.noResults')}
        multiple={multiple}
        selectedIds={values}
        disabled={disabled}
        isLoading={isLoading}
        isError={isError}
        onRetry={onRetry}
        onQueryChange={setQuery}
        onSelect={(option) => {
          onChange(
            multiple
              ? values.includes(option.id)
                ? values.filter((key) => key !== option.id)
                : [...values, option.id]
              : option.id
                ? [option.id]
                : [],
          );
          if (!multiple) setQuery('');
        }}
      />
      {!multiple && selected[0]?.hint ? (
        <p className="text-xs text-muted-foreground">{selected[0].hint}</p>
      ) : null}
    </div>
  );
}
